package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLButtonElement
import kotlin.random.Random
import dev.lingvo.site.i18n.t

enum class LConfirmTone {
    NEUTRAL,
    DANGER,
}

@Composable
fun LConfirmDialog(
    open: Boolean,
    title: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier,
    tone: LConfirmTone = LConfirmTone.DANGER,
    consequenceTitle: String? = null,
    consequence: String? = null,
    typeToConfirm: String? = null,
    typeToConfirmLabel: String? = null,
    busy: Boolean = false,
    /**
     * What the dialog says while [busy], when the generic sentence is not enough.
     *
     * "Deleting project…" tells a reader which of the irreversible things they started is the one
     * still running; "Finishing" tells them only that something is. Defaulted, because most
     * confirmations are over in a moment and the generic sentence is right for those.
     */
    busyLabel: String? = null,
    error: String? = null,
    content: (@Composable () -> Unit)? = null,
) {
    val scope = remember { "lingvo-confirm-${Random.nextInt(1_000_000)}" }
    val typedFieldId = "$scope-typed"
    val confirmId = "$scope-confirm"
    val errorId = "$scope-error"
    var typedValue by remember(open, typeToConfirm) { mutableStateOf("") }
    val typeMatches = typeToConfirm == null || typedValue.trim() == typeToConfirm
    LaunchedEffect(open, typeToConfirm) {
        if (open && typeToConfirm != null) {
            delay(50)
            val input = document.querySelector("#$typedFieldId input:not(:disabled)") as? HTMLElement
            input?.focus()
        }
    }

    val close = { if (!busy) onCancel() }

    // **Focus follows the recovery.** A failed confirmation leaves the reader wherever the browser
    // put them — in practice on `body`, because the button they pressed was disabled while the
    // request was out and then re-enabled underneath them. The error is announced by the alert
    // below; this puts the keyboard back on the control that retries it, which is the only thing
    // they can do next.
    LaunchedEffect(error, busy) {
        if (error == null || busy) return@LaunchedEffect
        // Waits for the button to be focusable rather than assuming one frame is enough. The
        // control was `disabled` while the request was out, and a disabled element refuses focus
        // silently — so a single `delay` that lands early does nothing and leaves no trace.
        repeat(FOCUS_ATTEMPTS) {
            delay(FOCUS_SETTLE_MS)
            val button = document.getElementById(confirmId) as? HTMLButtonElement
            if (button != null && !button.disabled) {
                button.focus()
                return@LaunchedEffect
            }
        }
    }
    LModal(
        open = open,
        title = title,
        onClose = close,
        modifier = modifier,
        size = LModalSize.SM,
        actions = {
            LButton(t("ds.ds.lconfirm_dialog.cancel", "Cancel"), close, variant = LButtonVariant.GHOST, enabled = !busy)
            LButton(
                label = confirmLabel,
                onClick = onConfirm,
                modifier = Modifier
                    .attr("id", confirmId)
                    .then(if (error != null) Modifier.attr("aria-describedby", errorId) else Modifier),
                variant = if (tone == LConfirmTone.DANGER) LButtonVariant.DANGER else LButtonVariant.PRIMARY,
                enabled = !busy && typeMatches,
            )
        },
    ) {
        Column(Modifier.gap(Space.MD)) {
            if (consequenceTitle != null && consequence != null) {
                LAlert(consequenceTitle, consequence, variant = if (tone == LConfirmTone.DANGER) {
                    LAlertVariant.DANGER
                } else {
                    LAlertVariant.INFO
                })
            }
            if (typeToConfirm != null) {
                Div(Modifier.fillMaxWidth().attr("id", typedFieldId).toAttrs()) {
                    LaunchedEffect(typedFieldId) {
                        delay(10)
                        (document.querySelector("#$typedFieldId input:not(:disabled)") as? HTMLElement)?.focus()
                    }
                    LTextField(
                        value = typedValue,
                        onValueChange = { typedValue = it },
                        modifier = Modifier.fillMaxWidth(),
                        label = typeToConfirmLabel ?: "Type $typeToConfirm to confirm",
                        error = if (typedValue.isNotBlank() && !typeMatches) {
                            "That is not $typeToConfirm."
                        } else {
                            null
                        },
                        enabled = !busy,
                    )
                }
            }
            if (content != null) content()
            if (busy) {
                // `role="status"` as well as `aria-live`. A bare `aria-live` announces a change to a
                // reader already on the element; the role is what makes this a *region* assistive
                // technology can find and a caller can point at, and a dialog that has just taken an
                // irreversible action is exactly where somebody goes looking for what is happening.
                P(
                    Modifier
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .fontSize(TypeScale.Small)
                        .margin(0.px)
                        .toAttrs {
                            attr("role", "status")
                            attr("aria-live", "polite")
                        },
                ) {
                    Text(
                        busyLabel
                            ?: t(
                                "ds.ds.lconfirm_dialog.finishing_this_cannot_be_cancelled",
                                "Finishing — this cannot be cancelled.",
                            ),
                    )
                }
            }
            if (error != null) {
                Div(Modifier.fillMaxWidth().attr("id", errorId).toAttrs()) {
                    LAlert(t("ds.ds.lconfirm_dialog.nothing_changed", "Nothing changed"), error, variant = LAlertVariant.DANGER, live = true)
                }
            }
        }
    }
}

/** One frame's grace between attempts, so focus lands after the recomposition that re-enables it. */
private const val FOCUS_SETTLE_MS = 50L

/** Half a second of trying, then give up rather than spin. */
private const val FOCUS_ATTEMPTS = 10
